import { Button, Spinner, Text } from "@ttc3k/trekker";
import { ArrowLeft, ArrowRight } from "iconoir-react";
import { useTranslation } from "react-i18next";
import { Box, Flex } from "styled-system/jsx";
import { SectionHeading } from "../SectionHeading/SectionHeading";
import { Events } from "../Events";
import { EventManyQuery } from "gql/generated";
import { useEventsWidgetContext } from "../EventsContext";
import { useFeaturedEvents } from "./useFeaturedEvents";
import { DateRange, EventWidgetSize } from "widgets/events/types";
import { ApolloError } from "@apollo/client";
import { EmptyState } from "shared/components";
import { featuredEventsRecipe } from "./recipe";

type FeaturedEventsProps = {
  events: EventManyQuery["eventMany"][0][];
  loading: boolean;
  error: ApolloError | undefined;
  dateRange: DateRange;
};

export const FeaturedEvents = ({
  events,
  loading,
  error,
  dateRange,
}: FeaturedEventsProps) => {
  const { t } = useTranslation("events", { keyPrefix: "WIDGET" });

  const { widgetSize } = useEventsWidgetContext();
  const { currentPage, eventsPerPage, totalPages, handlePageChange } =
    useFeaturedEvents({
      dateRange,
      events,
      widgetSize,
    });

  const classes = featuredEventsRecipe({
    size: widgetSize,
    view:
      dateRange.isFullMonth &&
      !dateRange.forceGridView &&
      !dateRange.showFutureEventNotice
        ? "month"
        : "selectedDates",
  });

  const isMobile = widgetSize === EventWidgetSize.Mobile;

  return (
    <Flex part="events__container-featured" flexDir="column" gap="400">
      <SectionHeading
        heading={
          dateRange.isFullMonth &&
          !dateRange.forceGridView &&
          !dateRange.showFutureEventNotice
            ? t("LABELS.FEATURED_EVENTS")
            : t("LABELS.EVENTS")
        }
      >
        <Flex
          flexDir="row"
          alignItems={"center"}
          gap="100"
          className={classes.heading}
        >
          <Text visual="smallTag" color="text.light" pr="50">
            {!isMobile && (
              <>
                {currentPage} {t("WORDS.OF")}
              </>
            )}{" "}
            {isMobile
              ? `${t("WORDS.EVENT", { count: events.length })}`
              : totalPages}
          </Text>
          {!isMobile && (
            <>
              <Button
                part="events__btn-prev"
                Icon={ArrowLeft}
                visual="primary"
                mode="light"
                borderRadius="full"
                onClick={() => handlePageChange(-1)}
                disabled={
                  currentPage === 1 || totalPages <= 1 || loading || error
                }
                w="32px"
                h="32px"
                boxShadow="0px 1px 4px 0px rgba(10, 10, 10, 0.20), 0px 1px 2px 0px rgba(10, 10, 10, 0.10)"
              />
              <Button
                part="events__btn-next"
                Icon={ArrowRight}
                visual="primary"
                mode="light"
                borderRadius="full"
                onClick={() => handlePageChange(1)}
                disabled={
                  currentPage === totalPages ||
                  totalPages <= 1 ||
                  loading ||
                  error
                }
                w="32px"
                h="32px"
                boxShadow="0px 1px 4px 0px rgba(10, 10, 10, 0.20), 0px 1px 2px 0px rgba(10, 10, 10, 0.10)"
              />
            </>
          )}
        </Flex>
      </SectionHeading>
      <Box className={classes.events}>
        {loading ? (
          <Flex
            w={"full"}
            h={"full"}
            minH="364px"
            justifyContent={"center"}
            alignItems={"center"}
          >
            <Spinner width={"40px"} height={"40px"} />
          </Flex>
        ) : error ? (
          <EmptyState
            part="events__featured-error"
            text={t("ERRORS.GENERAL")}
            h="full"
            minH="364px"
            backgroundColor="bg.lightest"
          />
        ) : events.length > 0 ? (
          <Events
            events={events}
            currentPage={currentPage}
            eventsPerPage={eventsPerPage}
            dateRange={dateRange}
          />
        ) : (
          <EmptyState
            part="events__featured-no-data"
            text={t("NO_DATA.FEATURED")}
            h="full"
            minH="364px"
            backgroundColor="bg.lightest"
          />
        )}
      </Box>
    </Flex>
  );
};
